'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button } from '@/components/Button';
import { BottomSheet } from '@/components/BottomSheet';

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Types
interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface TimeRangeOption {
  label: string;
  value: string;
}

interface DateRangeFilterProps {
  onDateRangeChange?: (range: DateRange) => void;
  className?: string;
  initialRange?: DateRange;
}

// Time range options
const timeRangeOptions: TimeRangeOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'Custom', value: 'custom' },
];

// Calendar component using HTML5 date inputs
const Calendar: React.FC<{
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  showDualCalendar?: boolean;
  maxRangeMonths?: number;
}> = ({
  range,
  onRangeChange,
  currentMonth,
  onMonthChange,
  showDualCalendar = true,
  maxRangeMonths = 3,
}) => {
  // Parse the input's YYYY-MM-DD as local midnight. `new Date('YYYY-MM-DD')`
  // parses as UTC midnight, which lands on the previous day in timezones
  // behind UTC and gives a 07:00 WIB timestamp instead of 00:00 here.
  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const parts = value.split('-');
    if (parts[0].length > 4) parts[0] = parts[0].slice(-4);
    const [y, m, d] = parts.map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const handleStartDateChange = (value: string) => {
    const newDate = parseInputDate(value);
    // Clear end date if it now exceeds start + maxRangeMonths
    const maxEnd = newDate
      ? dayjs(newDate).add(maxRangeMonths, 'month').toDate()
      : null;
    const clampedTo = range.to && maxEnd && range.to > maxEnd ? null : range.to;
    onRangeChange({ from: newDate, to: clampedTo });
  };

  const handleEndDateChange = (value: string) => {
    const newDate = parseInputDate(value);
    onRangeChange({ from: range.from, to: newDate });
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('YYYY-MM-DD');
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    onMonthChange(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    onMonthChange(nextMonth);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <button
          onClick={handlePrevMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <i className="ri-arrow-left-s-line text-gray-600"></i>
        </button>

        <span className="font-medium text-gray-900">
          {dayjs(currentMonth).format('MMMM YYYY')}
        </span>

        <button
          onClick={handleNextMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <i className="ri-arrow-right-s-line text-gray-600"></i>
        </button>
      </div>

      {/* Date Range Inputs */}
      <div className="flex-1 p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            max="9999-12-31"
            value={formatDateForInput(range.from)}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={formatDateForInput(range.to)}
            onChange={(e) => handleEndDateChange(e.target.value)}
            min={formatDateForInput(range.from)}
            max={
              range.from
                ? dayjs(range.from)
                    .add(maxRangeMonths, 'month')
                    .format('YYYY-MM-DD')
                : '9999-12-31'
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {range.from && (
            <p className="mt-1 text-xs text-gray-400">
              Max range: {maxRangeMonths} months from start date
            </p>
          )}
        </div>

        {/* Quick Calendar View */}
        <div className="mt-6">
          <div className="text-xs text-gray-500 mb-2">
            Current Month Calendar
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="font-medium text-gray-400 p-2">
                {day}
              </div>
            ))}
            {/* Leading blanks so day 1 lands on its actual weekday column */}
            {Array.from(
              { length: dayjs(currentMonth).startOf('month').day() },
              (_, i) => (
                <div key={`empty-${i}`} />
              )
            )}
            {Array.from(
              { length: dayjs(currentMonth).daysInMonth() },
              (_, i) => {
                const date = dayjs(currentMonth)
                  .date(i + 1)
                  .startOf('day')
                  .toDate();
                // When picking end (from set, to not set) — disable days outside ±maxRangeMonths from start
                const maxEnd =
                  range.from && !range.to
                    ? dayjs(range.from)
                        .add(maxRangeMonths, 'month')
                        .startOf('day')
                        .toDate()
                    : null;
                const minStart =
                  range.from && !range.to
                    ? dayjs(range.from)
                        .subtract(maxRangeMonths, 'month')
                        .startOf('day')
                        .toDate()
                    : null;
                const isDisabled = !!(
                  (maxEnd && date > maxEnd) ||
                  (minStart && date < minStart)
                );
                const isInRange =
                  range.from &&
                  range.to &&
                  date >= range.from &&
                  date <= range.to;
                const isStart =
                  range.from && dayjs(date).isSame(range.from, 'day');
                const isEnd = range.to && dayjs(date).isSame(range.to, 'day');

                return (
                  <button
                    key={i}
                    disabled={isDisabled}
                    onClick={() => {
                      if (!range.from || (range.from && range.to)) {
                        onRangeChange({ from: date, to: null });
                      } else {
                        if (date < range.from) {
                          // Flipped: date is before start — clamp so range ≤ maxRangeMonths
                          const minAllowed = dayjs(range.from)
                            .subtract(maxRangeMonths, 'month')
                            .toDate();
                          const clampedFrom =
                            date < minAllowed ? minAllowed : date;
                          onRangeChange({ from: clampedFrom, to: range.from });
                        } else {
                          // Normal: clamp end to start + maxRangeMonths
                          const maxAllowed = dayjs(range.from)
                            .add(maxRangeMonths, 'month')
                            .toDate();
                          const clampedTo =
                            date > maxAllowed ? maxAllowed : date;
                          onRangeChange({ from: range.from, to: clampedTo });
                        }
                      }
                    }}
                    className={`
                    p-2 text-xs rounded
                    ${isDisabled ? 'text-gray-300 cursor-not-allowed' : ''}
                    ${!isDisabled && (isStart || isEnd) ? 'bg-blue-500 text-white' : ''}
                    ${!isDisabled && isInRange ? 'bg-blue-100 text-blue-900' : ''}
                    ${
                      !isDisabled && !isInRange && !isStart && !isEnd
                        ? 'hover:bg-gray-100'
                        : ''
                    }
                  `}
                  >
                    {i + 1}
                  </button>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  className = '',
  initialRange = { from: null, to: null },
}) => {
  const getDefaultRange = (): DateRange => ({
    from: dayjs().subtract(3, 'month').startOf('day').toDate(),
    to: dayjs().startOf('day').toDate(),
  });

  const [range, setRange] = useState<DateRange>(
    initialRange.from || initialRange.to ? initialRange : getDefaultRange()
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(
    initialRange.from || initialRange.to ? 'custom' : 'last-3-months'
  );
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);

  const timeRangeRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Detect mobile viewport
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        timeRangeRef.current &&
        !timeRangeRef.current.contains(event.target as Node)
      ) {
        setIsTimeRangeOpen(false);
      }
      // On mobile the date picker is a BottomSheet portal — skip click-outside
      // so tapping inside the sheet (e.g. calendar dates) doesn't close it.
      if (
        !isMobile &&
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Removed automatic range change notification - only on Apply

  const getDateRangeForTimeOption = (option: string): DateRange => {
    // Helper function to create a local date at start of day
    const createLocalDate = (daysFromToday: number = 0): Date => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const day = now.getDate() + daysFromToday;

      // Create a new date with the calculated day, ensuring local timezone
      const date = new Date(year, month, day, 0, 0, 0, 0);
      return date;
    };

    switch (option) {
      case 'today':
        return {
          from: createLocalDate(0),
          to: createLocalDate(0),
        };
      case 'yesterday':
        return {
          from: createLocalDate(-1),
          to: createLocalDate(-1),
        };
      case 'last-7-days':
        return {
          from: createLocalDate(-7),
          to: createLocalDate(0),
        };
      case 'last-30-days':
        return {
          from: createLocalDate(-30),
          to: createLocalDate(0),
        };
      case 'last-3-months':
        return {
          from: dayjs().subtract(3, 'month').startOf('day').toDate(),
          to: createLocalDate(0),
        };
      case 'all-time':
        return { from: null, to: null };
      default:
        return { from: null, to: null };
    }
  };

  const handleTimeRangeSelect = (option: string) => {
    setSelectedTimeRange(option);
    setIsTimeRangeOpen(false);

    setIsDatePickerOpen(true);

    if (option !== 'custom') {
      const newRange = getDateRangeForTimeOption(option);
      setRange(newRange);

      // Update calendar view to show the start date of the selected range
      if (newRange.from) {
        setCurrentMonth(newRange.from);
      } else {
        setCurrentMonth(new Date());
      }
    }
  };

  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setRange(newRange);
    // Reset time range selection when manually selecting dates
    if (newRange.from || newRange.to) {
      setSelectedTimeRange('custom');
    }
  }, []);

  const handleApplyDateRange = () => {
    setIsDatePickerOpen(false);
    onDateRangeChange?.(range);
  };

  const handleClearDateRange = () => {
    const defaultRange: DateRange = {
      from: dayjs().subtract(3, 'month').startOf('day').toDate(),
      to: dayjs().startOf('day').toDate(),
    };
    setRange(defaultRange);
    setSelectedTimeRange('last-3-months');
    onDateRangeChange?.(defaultRange);
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.from) return 'Select date range';

    if (!range.to) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    if (dayjs(range.from).isSame(range.to, 'day')) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    return `${dayjs(range.from).format('MMM DD')} - ${dayjs(range.to).format(
      'MMM DD, YYYY'
    )}`;
  };

  const getSelectedTimeRangeLabel = () => {
    const option = timeRangeOptions.find(
      (opt) => opt.value === selectedTimeRange
    );
    return option?.label || 'All Time';
  };

  return (
    <div className={`flex flex-col md:inline-flex md:flex-row ${className}`}>
      {/* Time Range Dropdown */}
      <div className="relative w-full md:w-auto" ref={timeRangeRef}>
        <button
          onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
          className="flex w-full min-w-[210px] md:min-w-0 md:w-auto items-center gap-2 px-3 py-2 border border-gray-300 rounded-md md:rounded-l-md md:rounded-r-none bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <span className="flex-1 text-center md:flex-none md:text-left truncate">
            {getSelectedTimeRangeLabel()}
          </span>
          <i
            className={`ri-arrow-down-s-line transition-transform ${
              isTimeRangeOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </button>

        {isTimeRangeOpen && (
          <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="p-1">
              {timeRangeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleTimeRangeSelect(option.value)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                    selectedTimeRange === option.value
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Date Picker Button */}
      <div className="relative w-full md:w-auto" ref={datePickerRef}>
        <button
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          className="flex w-full min-w-[210px] md:min-w-0 md:w-auto items-center gap-2 px-3 py-2 border md:border-l-0 border-gray-300 rounded-md md:rounded-r-md md:rounded-l-none bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <i className="ri-calendar-line text-gray-600"></i>
          <span className="flex-1 text-center md:flex-none md:text-left truncate max-w-48">
            {selectedTimeRange === 'custom'
              ? formatDateRange(range)
              : 'Custom Range'}
          </span>
        </button>

        {/* Desktop dropdown */}
        {!isMobile && isDatePickerOpen && (
          <div
            className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg"
            style={{ width: '400px', minHeight: '610px' }}
          >
            <div className="flex flex-col h-full rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-900">
                  Select Date Range
                </h3>
                <button
                  onClick={() => setIsDatePickerOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Calendar */}
              <Calendar
                range={range}
                onRangeChange={handleDateRangeChange}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
                maxRangeMonths={3}
              />

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {range.from && range.to
                    ? `${dayjs(range.from).format('MMM DD, YYYY')} - ${dayjs(
                        range.to
                      ).format('MMM DD, YYYY')}`
                    : range.from
                      ? dayjs(range.from).format('MMM DD, YYYY')
                      : 'No range selected'}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant="neutral"
                    onClick={handleClearDateRange}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={handleApplyDateRange}
                    disabled={!range.from}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile bottom sheet */}
        {isMobile && (
          <BottomSheet
            isOpen={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            title="Select Date Range"
            defaultSnap="100dvh"
            footer={
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {range.from && range.to
                    ? `${dayjs(range.from).format('MMM DD, YYYY')} - ${dayjs(
                        range.to
                      ).format('MMM DD, YYYY')}`
                    : range.from
                      ? dayjs(range.from).format('MMM DD, YYYY')
                      : 'No range selected'}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant="neutral"
                    onClick={handleClearDateRange}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={handleApplyDateRange}
                    disabled={!range.from}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            }
          >
            <Calendar
              range={range}
              onRangeChange={handleDateRangeChange}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              maxRangeMonths={3}
            />
          </BottomSheet>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
